import {
  Alert,
  AlertIcon,
  createStandaloneToast,
  Flex,
  HStack,
  Icon,
  Text,
  Grid,
  Link,
  Box,
  Textarea,
} from "@chakra-ui/react";
import { Mixpanel } from "../Services/MixPanel";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { BsCalendarFill } from "react-icons/bs";
import { CommonMethodService } from "../Services/CommonMethodService";
import { IBSAdonisService, IResponse } from "../Services/IBSAdonisService";
import { IBodyshopService } from "../UserService";
import {
  Appointment,
  Email,
  IHoliday,
  IInspectionAppointmentPayload,
} from "../modals/Appointment";
import { screens } from "../modals/Enums/Screens";
import BookingCalendar from "./BookingCalendar/BookingCalendar";
import { AdminChecklistItems, BookingInspectionProps } from "../CommonProps";
import BookingConfirmed from "./BookingConfirmed/BookingConfirmed";
import LoaderCommon from "../Loader/LoaderCommon";
import CompanyMap from "./CompanyMap";
import { MdLocationOn } from "react-icons/md";
import UploadPhotos, { IFile } from "../UploadPhotos/UploadPhotos";
import UploadImagesModel from "../UploadImagesModel/UploadImagesModel";
import { createEvent } from "./CreateEvent";
import {
  filteredAvailableSlotsFromBookSlots,
  formatTime,
  getDisabledDates,
  findClosestPastTimeSlot,
  getWeekStartAndEnd,
  groupByDayOfWeek,
} from "../CommonService";

export interface ISlot {
  time: string;
  occupiedDropOff: boolean;
  index?: number;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const toast = createStandaloneToast();
const BookInspection = ({
  estimateDetails,
  handleDashboardSelectedTab,
}: BookingInspectionProps) => {
  const inspectionBookingRequest = "Inspection Booking Request Confirmation";
  const assessmentStatus = "Assessment Pending.";
  const defaultSMSId = "f920750d-42d2-4e7f-8663-ed5b2da9c6e2";
  const defaultEmail = "153440a5-d1ea-4e04-83b8-c0dd81a60c78";
  const mapIframe = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3156.6110651134736!2d152.9913919!3d-27.41808!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b915774ed913c9b%3A0x787592295017734!2sFlow+Autobody!5e0!3m2!1sen!2sau!4v1666168111046!5m2!1sen!2sau`;

  const commonMethodService = new CommonMethodService();

  const [selectedDate, setSelectedDate] = useState<string | null | any>(null);
  const [slots, setSlots] = useState<Array<ISlot>>([]);
  const [selectedSlot, setSelectedSlot] = useState<ISlot | null | any>(null);
  const [filesArray, setFilesArray] = useState<IFile[]>([]);
  const [availableAppointmentsDates, setAvailableAppointmentsDates] = useState<
    {
      showLoader: boolean | undefined | null;
      dueDate: string | Moment;
      index: Number;
    }[]
  >([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [screenLoader, setScreenLoader] = useState<boolean>(false);
  const [isBookingInspectionBooked, setIsBookingInspectionBooked] =
    useState<boolean>(false);
  const [bookingDate, setBookingDate] = useState<Moment | null | string>(null);
  const [bookingDueDate, setBookingDueDate] = useState<Moment | null | string>(
    null
  );
  const [availableTimeSlotsResponse, setAvailableTimeSlotsResponse] =
    useState<any>([]);
  const [fromDate, setFromDate] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [bookingInspectionCompleted, setBookingInspectionCompleted] =
    useState<boolean>(false);
  const [imageUploadModal, setImageUploadModal] = useState<boolean>(false);
  const [previousFromDate, setPreviousFromDate] = useState<string>("");
  const [previousComment, setPreviousComment] = useState<string>("");

  const iBodyShopService = new IBodyshopService();
  const ibsAdonisService = new IBSAdonisService();

  const generateDatesForCalendar = async (bookedDate?: string) => {
    const fromDate = moment().startOf("day");
    const toDate = moment().add(3, "months").startOf("day");

    const formattedFromDateForPublicHoliday = fromDate.format("YYYY-MM-DD");
    const formattedToDateForPublicHoliday = toDate.format("YYYY-MM-DD");
    const { start, end } = getWeekStartAndEnd(`${new Date()}`, true);
    const bookSlotsResponse: Appointment[] =
      await ibsAdonisService.getBookSlots({
        start,
        end,
      });
    const response = await ibsAdonisService.getInspectionTimeSlots();
    const disabledDates = getDisabledDates(response, bookSlotsResponse); // Custom disabled dates
    const filterDisabledDateIfAlreadyBookedBySameUser = disabledDates?.filter(
      (date) => {
        return date !== bookedDate;
      }
    );

    const publicHolidayResponse: IHoliday[] =
      await ibsAdonisService.getPublicHoliday({
        start: formattedFromDateForPublicHoliday,
        end: formattedToDateForPublicHoliday,
      });

    // Extract public holiday dates for comparison
    const publicHolidayDates = publicHolidayResponse?.map((holiday: any) =>
      moment(holiday.holidayDate).format("YYYY-MM-DD")
    );

    const startDate = moment().startOf("day");
    const endDate = moment().add(3, "months").startOf("day");
    const dates = [];
    let currentDate = startDate.clone();
    let indexNo = 0;
    while (currentDate.isBefore(endDate)) {
      // Skip weekends (Saturday: 6, Sunday: 0)
      if (currentDate.day() !== 6 && currentDate.day() !== 0) {
        // Skip public holidays
        if (
          !publicHolidayDates.includes(currentDate.format("YYYY-MM-DD")) &&
          !filterDisabledDateIfAlreadyBookedBySameUser.includes(
            currentDate.format("YYYY-MM-DD")
          )
        ) {
          dates.push({
            dueDate: currentDate.format("YYYY-MM-DDTHH:mm:ss.SSS"),
            showLoader: false,
            index: indexNo,
          });
          indexNo++;
        }
      }
      currentDate.add(1, "day");
    }
    setAvailableAppointmentsDates(dates);
  };

  const getInspectionIBodyShopAvailableSlots = async () => {
    // Fetch available slots from the service
    const response = await ibsAdonisService.getInspectionTimeSlots();
    const timeSlots = response;
    // Check if timeSlots has data
    if (timeSlots?.length) {
      const availableTimeSlots =
        findClosestPastTimeSlot(timeSlots)?.sortedTimeSlots || [];
      const formattedSlots = availableTimeSlots.map(
        ({ dayOfWeek, startTime, minutesDuration }) => ({
          dayOfWeek,
          minutesDuration,
          startTime: formatTime(startTime),
        })
      );
      setAvailableTimeSlotsResponse(response);
      return formattedSlots;
    }

    // Return an empty object if no slots are available
    return {};
  };

  const isBookingInspectionExistChecking = async () => {
    setScreenLoader(true);
    const bookingExistRecord = await ibsAdonisService.fetchInspectionData(
      String(estimateDetails.estimateNumber)
    );

    if (bookingExistRecord) {
      const startDate = moment(bookingExistRecord?.dueDate).startOf("day");
      let userSelectedDate = startDate
        .clone()
        .format("YYYY-MM-DDTHH:mm:ss.SSS");

      // For Showing Selected Date & Available Slots
      const time = moment(bookingExistRecord?.dueDate).format("hh:mm A");

      // Code For Getting Slots
      const { start, end } = getWeekStartAndEnd(userSelectedDate);
      // Fetch available book slots for the selected week
      const bookSlotsResponse: Appointment[] =
        await ibsAdonisService.getBookSlots({
          start,
          end,
        });

      const response = await ibsAdonisService.getInspectionTimeSlots();

      const bookSlotsResponseArray = bookSlotsResponse
        ?.filter(
          ({ dueDate }: { dueDate: string }) =>
            dueDate != bookingExistRecord?.dueDate
        )
        ?.map(({ dueDate }: { dueDate: string }) => dueDate);

      const formattedDueDate = moment(bookingExistRecord?.dueDate).format(
        "YYYY-MM-DD"
      );

      const availableTimeSlots =
        findClosestPastTimeSlot(response, formattedDueDate)?.sortedTimeSlots ||
        [];

      // Filter available slots based on the fetched data
      const filteredAvailableSlotsArray = filteredAvailableSlotsFromBookSlots(
        availableTimeSlots,
        bookSlotsResponseArray
      );

      // Group available time slots by day of the week
      const groupAvailableTimeSlots = groupByDayOfWeek(
        filteredAvailableSlotsArray
      );
      const dayName = moment(userSelectedDate).format("dddd");
      const timeSlotsForTheSelectedDay = groupAvailableTimeSlots[dayName] || [];

      // Prepare updated slots for the selected day
      const updatedSlots: Array<ISlot> = timeSlotsForTheSelectedDay.map(
        ({ startTime }: { startTime: string }, i: number) => ({
          time: moment(startTime, "HH:mm:ss").format("hh:mm A"),
          occupiedDropOff: false,
          isLoading: false,
          index: i,
          isDisabled: false,
        })
      );

      const findSelectedSlot = updatedSlots?.find((item: any) => {
        const formattedSlotTime = moment(item.time, "h:mm A").format("h:mm A");
        const formattedTime = moment(time, "hh:mm A").format("h:mm A");
        return formattedSlotTime === formattedTime;
      });

      setSlots(updatedSlots);
      setFromDate(bookingExistRecord?.dueDate);
      setBookingDate(userSelectedDate);
      setSelectedDate(userSelectedDate);
      setSelectedSlot(findSelectedSlot);
      setBookingDueDate(bookingExistRecord?.dueDate);
      setIsBookingInspectionBooked(true);
      setComment(bookingExistRecord?.note);

      // For Comparing Previous State Of date and commits
      setPreviousFromDate(bookingExistRecord?.dueDate);
      setPreviousComment(bookingExistRecord?.note);
    }
    await generateDatesForCalendar(
      moment(bookingExistRecord?.dueDate).format("YYYY-MM-DD") ?? undefined
    );
    await getInspectionIBodyShopAvailableSlots();
    setScreenLoader(false);
  };

  useEffect(() => {
    isBookingInspectionExistChecking();
  }, []);

  useEffect(() => {
    const isOnBoardingFlow = estimateDetails.adminCheckList.some(
      (checklistItem: string) =>
        checklistItem.includes(AdminChecklistItems.ONBOARDING_FLOW)
    );
    const isBookingFlow = estimateDetails.adminCheckList.some(
      (checklistItem: string) =>
        checklistItem.includes(AdminChecklistItems.BOOKING_FLOW)
    );
    if ((!isOnBoardingFlow && !isBookingFlow) || isBookingFlow) {
      commonMethodService.setQueryParams(
        estimateDetails.estimateNumber,
        "home"
      );
      handleDashboardSelectedTab(screens.home);
    }
    Mixpanel.track("Book Inspection Viewed", {
      estimateNumber: estimateDetails.estimateNumber,
      estimateId: estimateDetails.estimateId,
    });
  }, []);

  const getSMSTemplate = async () => {
    try {
      const SMSTemplates = await ibsAdonisService.textMessageSuggestions(
        estimateDetails.estimateId
      );
      if (SMSTemplates) {
        return (
          SMSTemplates?.find((sms) => sms?.id === defaultSMSId)?.message || ""
        );
      }
    } catch (error) {
      console.error("🚀 ~ Booking ~ getSMSTemplate ~ error:", error);
      return "";
    }
  };

  const sendEmails = async () => {
    try {
      const emailTemplates = await ibsAdonisService.getEmailTemplates(
        estimateDetails.estimateId
      );

      const selectedEmailTemplate = emailTemplates.find((template: any) => {
        return template.id === defaultEmail;
      });

      if (selectedEmailTemplate) {
        const emailTemplate: Email = {
          attachments: [],
          body: selectedEmailTemplate.message,
          fromName: "Flow Autobody",
          subject:
            "Inspection is Confirmed: " +
            estimateDetails.estimateNumber.toString(),
          to: [
            estimateDetails.email
              ? estimateDetails.email
              : "hello@flowautobody.com.au",
          ],
          assignedToId: estimateDetails.estimateId.toString(),
          jobNumber: estimateDetails.estimateNumber,
          bodyIsHtml: true,
          cc: ["hello@flowautobody.com.au"],
        };

        const responseOfSendEmail: any =
          await ibsAdonisService.sendEmailWithTemplate(emailTemplate);
        if (responseOfSendEmail.success) {
          Mixpanel.track("Customer Email Send", {
            emailTemplate: emailTemplate,
            estimateDetail: estimateDetails,
            status: "Email send successfully",
          });
        } else {
          Mixpanel.track("Customer Email Send Error", {
            emailTemplate: emailTemplate,
            estimateDetail: estimateDetails,
            error: responseOfSendEmail?.errors[0]?.message,
            status: "Email not send",
          });
        }
      }
    } catch (error) {
      console.error("🚀 ~ sendEmails ~ error:", error);
    }
  };

  const sendDefaultSMS = async () => {
    // Create the default SMS object
    let defaultSMS = {
      estimateId: estimateDetails.estimateId,
      body: await getSMSTemplate(),
    };
    try {
      // Send the SMS
      const response: any = await ibsAdonisService.sendSMS(defaultSMS);
      // If the SMS is sent successfully, track it
      if (response?.success) {
        Mixpanel.track("Sms Send", {
          defaultSMS,
          estimateDetail: estimateDetails,
          status: "Sms send successfully",
        });
      } else {
        // Track the failed SMS attempt in Mixpanel if response.success is false
        Mixpanel.track("Sms Send Error", {
          defaultSMS,
          estimateDetail: estimateDetails,
          status: "Sms not send",
          error: response?.message || "Unknown error", // Using response message if available
        });
      }
    } catch (error) {
      // Track the error in Mixpanel
      Mixpanel.track("Sms Send Error", {
        defaultSMS,
        estimateDetail: estimateDetails,
        error,
        status: "Sms not send",
      });
    }
  };

  const onDateSelected = async (date: Moment) => {
    try {
      // Create a copy of the state to avoid mutating the original array
      const allAvailableAppointmentsDates = [...availableAppointmentsDates];

      // Check if any date already has a loader active
      const isLoading = allAvailableAppointmentsDates.some(
        (date) => date.showLoader
      );
      if (isLoading) {
        return; // Prevent further execution if a loader is active
      }

      // Find the date in the array
      const findDate = allAvailableAppointmentsDates?.find(({ dueDate }) =>
        moment(dueDate).isSame(date, "day")
      );

      if (!findDate) {
        console.error("Selected date not found in available appointments.");
        return;
      }

      // Update the `showLoader` flag for the selected date
      const updatedAppointmentsDates = allAvailableAppointmentsDates.map(
        (appointment) =>
          moment(appointment.dueDate).isSame(date, "day")
            ? { ...appointment, showLoader: true }
            : appointment
      );

      setAvailableAppointmentsDates(updatedAppointmentsDates);

      const dayName = moment(date).format("dddd");
      const { start, end } = getWeekStartAndEnd(date);

      // Fetch available book slots for the selected week
      const bookSlotsResponse: Appointment[] =
        await ibsAdonisService.getBookSlots({ start, end });

      const bookSlotsResponseArray = bookSlotsResponse
        ?.filter(
          ({ dueDate }: { dueDate: string }) => dueDate !== bookingDueDate
        )
        ?.map(({ dueDate }: { dueDate: string }) => dueDate);
      const formattedSelectedDate = moment(date).format("YYYY-MM-DD");
      const availableTimeSlots =
        findClosestPastTimeSlot(
          availableTimeSlotsResponse,
          formattedSelectedDate
        )?.sortedTimeSlots || [];

      const filteredAvailableSlotsArray = filteredAvailableSlotsFromBookSlots(
        availableTimeSlots,
        bookSlotsResponseArray
      );

      const groupAvailableTimeSlots = groupByDayOfWeek(
        filteredAvailableSlotsArray
      );

      const timeSlotsForTheSelectedDay = groupAvailableTimeSlots[dayName] || [];
      const updatedSlots: Array<ISlot> = timeSlotsForTheSelectedDay.map(
        ({ startTime }: { startTime: string }, i: number) => ({
          time: startTime,
          occupiedDropOff: false,
          isLoading: false,
          index: i,
          isDisabled: false,
        })
      );

      // Reset the loader flag
      const finalAppointmentsDates = updatedAppointmentsDates.map(
        (appointment) =>
          moment(appointment.dueDate).isSame(date, "day")
            ? { ...appointment, showLoader: false }
            : appointment
      );

      setAvailableAppointmentsDates(finalAppointmentsDates);

      setSelectedDate(findDate.dueDate);
      setSlots(updatedSlots);
      setBookingDate(date);

      // Reset Previous Date and Comment on Date Selection Because after any changes confirm Button Will Show
      setPreviousFromDate("");
      setPreviousComment("");
    } catch (error) {
      console.error("Error selecting date:", error);
    }
  };

  const bookingAllowance = {
    estimatedArrivalStartInterval: 0,
    estimatedCompletion: 0,
    estimatedCompletionCollectionInterval: 0,
  };

  const onSlotPicked = (slot: ISlot) => {
    const fromDate = moment(selectedDate);
    const time = moment(slot?.time, "hh:mm A");
    fromDate.set({
      hour: time.get("hour"),
      minute: time.get("minute"),
      second: time.get("second"),
    });
    const formattedFromDate = fromDate.format("YYYY-MM-DDTHH:mm:ss.SSS");
    setSelectedSlot(slot);
    setFromDate(formattedFromDate);
  };

  const sendTeamsNotification = async (created: boolean) => {
    const htmlContent = `<p> <strong>Booking Inspection request for </strong> ${
      estimateDetails.estimateNumber
    } <br>
    <strong>Booking date: </strong>${moment(fromDate).format(
      "dddd, DD/MM/YYYY"
    )}<br><br>
    ${
      estimateDetails.ownerName
        ? `<strong>Customer: </strong> ${estimateDetails.ownerName}<br>`
        : ""
    }<strong>Estimator: </strong> @${estimateDetails.estimatorName || ""}<br>
    <strong>IBS:</strong> <a href="https://app.ibodyshop.com/#/Estimate/${
      estimateDetails.estimateId
    }/header">link</a> </p>`;
    await ibsAdonisService.sendTeamsNotification({
      message: htmlContent,
      email: estimateDetails.email,
      note: {
        message: `Booking for onsite Inspection has been ${
          created ? "created" : "updated"
        }`,
        estimateId: estimateDetails.estimateId,
        title: `Booking Inspection`,
        estimateNumber: estimateDetails.estimateNumber,
      },
    });
  };

  const handleConfirmButton = async () => {
    setShowLoader(true);
    const bookingExistRecord = await ibsAdonisService.fetchInspectionData(
      String(estimateDetails.estimateNumber)
    );
    let payload: IInspectionAppointmentPayload = {
      dueDate: fromDate,
    };
    if (!bookingExistRecord) {
      payload = {
        ...payload,
        estimateId: estimateDetails.estimateId,
        estimateNo: estimateDetails.estimateNumber,
        note: comment,
        summary: estimateDetails.vehicleDescription || "(Unknown Vehicle)",
      };
      await ibsAdonisService
        .createInspectionAppointment(payload)
        .then(async (res: IResponse) => {
          if (res.IsSuccess) {
            await sendTeamsNotification(true);
            commonMethodService.setQueryParams(
              estimateDetails.estimateNumber,
              screens.bookingInspection
            );
            await sendEmails();
            await sendDefaultSMS();
            setBookingInspectionCompleted(true);
            setShowLoader(false);
            setPreviousFromDate(fromDate);
            setPreviousComment(comment);
            toast({
              title: "",
              description: `Inspection Booked successfully`,
              status: "success",
              duration: 1500,
              isClosable: true,
            });
          }
        });
    } else {
      payload = {
        ...payload,
        aggregateRootId: bookingExistRecord.id,
        note: comment,
      };
      ibsAdonisService
        .updateInspectionAppointment(payload)
        .then(async (res: IResponse) => {
          if (res.IsSuccess) {
            await sendTeamsNotification(false);
            await sendEmails();
            await sendDefaultSMS();
            commonMethodService.setQueryParams(
              estimateDetails.estimateNumber,
              screens.bookingInspection
            );
            setBookingInspectionCompleted(true);
            setIsBookingInspectionBooked(true);
            setShowLoader(false);
            setPreviousFromDate(fromDate);
            setPreviousComment(comment);
            toast({
              title: "",
              description: `Inspection Booking updated successfully`,
              status: "success",
              duration: 1500,
              isClosable: true,
            });
          }
        });
    }
  };

  const btnHideConditionForUnchangedState =
    fromDate === previousFromDate &&
    comment === previousComment &&
    previousFromDate !== "";

  return (
    <>
      {screenLoader ? (
        <LoaderCommon isLoginLoader={false} loadingText={""} />
      ) : (
        <>
          {bookingInspectionCompleted ? (
            <BookingConfirmed
              onClick={() => handleDashboardSelectedTab(screens.home)}
              onCountdownComplete={() => {
                setIsBookingInspectionBooked(true);
                setBookingInspectionCompleted(false);
              }}
              bookingText="Inspection Confirmed"
              bookingId={estimateDetails.estimateNumber.toString()}
              inspectionDate={moment(selectedDate)?.format("dddd, DD/MM/YYYY")}
              inspectionTime={selectedSlot?.time}
            />
          ) : bookingInspectionCompleted === false &&
            isBookingInspectionBooked === false ? (
            <>
              <div className="uploadImagesContainer">
                <button
                  className="booking-next-btn"
                  onClick={() => setImageUploadModal(true)}
                >
                  Upload Images
                </button>
              </div>
              <BookingCalendar
                estimateDetails={estimateDetails}
                ibsService={iBodyShopService}
                availableAppointments={availableAppointmentsDates}
                onDateSelected={(date: Moment) => {
                  if (!showLoader) {
                    setSelectedSlot(null);
                    onDateSelected(date);
                  }
                }}
                bookingAllowance={bookingAllowance}
                selectedDate={bookingDate}
                customQADetails={null}
                slots={slots}
                onSlotPicked={onSlotPicked}
                isSelected={selectedSlot}
                handleConfirm={handleConfirmButton}
                showLoader={showLoader}
                commentBox={
                  <Box mb={4} mt={"-25px"}>
                    <Textarea
                      value={comment}
                      placeholder="Enter Comments"
                      className="commentBox"
                      onChange={(e) => setComment(e?.target?.value)}
                    />
                  </Box>
                }
                isButtonDisabledForNonStateChanged={
                  btnHideConditionForUnchangedState
                }
              />
            </>
          ) : (
            <Flex
              mb={4}
              p={4}
              boxShadow={"base"}
              borderRadius={4}
              flexFlow={"column"}
            >
              <Text
                fontSize="xl"
                fontWeight={"bold"}
                color={"blue.700"}
                textTransform={"uppercase"}
                mb={4}
              >
                {inspectionBookingRequest}
              </Text>
              <Alert
                mb={4}
                borderRadius={4}
                status="success"
                variant="subtle"
                alignItems={"start"}
                textAlign={"left"}
              >
                <AlertIcon />
                {assessmentStatus}
              </Alert>
              <HStack mb={4}>
                <Icon
                  as={BsCalendarFill}
                  w={10}
                  h={10}
                  mr={2}
                  color={"blue.500"}
                />
                <Flex gap={"20px"} textAlign={"left"}>
                  <div>
                    <Text fontSize={"small"} fontWeight={"bold"}>
                      Inspection Date
                    </Text>
                    <Text fontSize={"medium"}>
                      {moment(selectedDate)?.format("dddd, DD/MM/YYYY")}
                    </Text>
                    <Text
                      fontSize={"small"}
                      textDecoration={"underline"}
                      cursor={"pointer"}
                      marginLeft={"2px"}
                      marginTop={"3px"}
                      onClick={() => {
                        createEvent(selectedDate);
                      }}
                    >
                      Add To Calendar
                    </Text>
                  </div>
                  <Flex flexFlow={"column"} textAlign={"left"}>
                    <Text fontSize={"small"} fontWeight={"bold"}>
                      Time
                    </Text>
                    <Text fontSize={"medium"}>{selectedSlot?.time}</Text>
                  </Flex>
                </Flex>
              </HStack>
              <HStack mb={4}>
                <Icon
                  as={MdLocationOn}
                  w={10}
                  h={10}
                  mr={2}
                  color={"blue.500"}
                />
                <Flex flexFlow={"column"} textAlign={"left"}>
                  <Text fontSize={"small"} fontWeight={"bold"}>
                    Location
                  </Text>
                  <Link
                    href={
                      "https://www.google.com/maps/place/Flow+Autobody/@-27.41808,152.9913919,17z/data=!4m13!1m7!3m6!1s0x6b9157748dcf3b43:0x975c92977c38c430!2s256+S+Pine+Rd,+Enoggera+QLD+4051!3b1!8m2!3d-27.41808!4d152.9935806!3m4!1s0x6b915774ed913c9b:0x787592295017734!8m2!3d-27.41808!4d152.9935806"
                    }
                    isExternal
                  >
                    <Text fontSize={"medium"}>
                      256 S Pine Rd, Enoggera QLD 4051
                    </Text>
                  </Link>
                </Flex>
              </HStack>
              <Box justifyContent="center" alignItems="center" mb={4}>
                <CompanyMap mapIframe={mapIframe} />
              </Box>
              <HStack spacing={4} className="btn-container">
                <button
                  className="booking-next-btn"
                  onClick={() => {
                    setIsBookingInspectionBooked(false);
                    Mixpanel.track("Reschedule Inspection Toggled", {
                      action: "Rescheduled",
                      status: "Inspection Rescheduled",
                    });
                  }}
                >
                  Reschedule Inspection
                </button>
              </HStack>
            </Flex>
          )}
        </>
      )}
      <UploadImagesModel
        isOpen={imageUploadModal}
        closeCallback={() => setImageUploadModal(false)}
      >
        <UploadPhotos
          filesArray={filesArray}
          setFilesArray={setFilesArray}
          setIsDeleting={() => {}}
          isOptional
        />
        <Grid className="btn-container">
          <button
            className="booking-next-btn mt-3"
            onClick={() => setImageUploadModal(false)}
          >
            <span>Close</span>
          </button>
        </Grid>
      </UploadImagesModel>
    </>
  );
};

export default BookInspection;
